import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { usePaletteCommands } from '../context/CommandPaletteContext';
import { Plus, Play, Bookmark, X as XIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowUpRight, ArrowDownRight, Minus, ChevronDown, ChevronRight,
  Loader2, TrendingUp, TrendingDown, DollarSign, Clock, Shield,
  BarChart3, Brain, Copy, RefreshCw, Download, FileText, Pencil,
  Check, X, Trash2, BookOpen, HelpCircle, Trophy
} from 'lucide-react';
import { toast } from 'sonner';
import { getCurrencySymbol } from '../lib/currency';

import SectionGuide, { resetGuides } from '../components/SectionGuide';
import PageHelpButton from '../components/PageHelpButton';
import MathExplainer, { FormulaBlock, CalcStep, MathSection } from '../components/MathExplainer';
import EmailShareDialog from '../components/EmailShareDialog';
import NominateWinnerDialog from '../components/NominateWinnerDialog';
import ReleaseImpactHeatmap from '../components/compare/ReleaseImpactHeatmap';
import WhatIfEditor from '../components/compare/WhatIfEditor';
import LiveKPIRail from '../components/LiveKPIRail';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Radar
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = { PM: 'Project Manager', CS: 'Consultant', CF: 'Config Specialist', IN: 'Integrations', DM: 'Data Migration', DE: 'Developer', IF: 'Infrastructure' };

// ── Delta Badge ──────────────────────────────────────────────
function DeltaBadge({ value, format = 'number', invert = false }) {
  if (value === 0) return <span className="text-slate-400 text-xs flex items-center gap-0.5"><Minus className="w-3 h-3" /> No change</span>;
  const positive = invert ? value < 0 : value > 0;
  const color = positive ? 'text-emerald-600' : 'text-red-600';
  const Icon = value > 0 ? ArrowUpRight : ArrowDownRight;
  const formatted = format === 'currency' ? `$${Math.abs(value).toLocaleString()}` : format === 'percent' ? `${Math.abs(value).toFixed(1)}%` : `${Math.abs(value).toLocaleString()}`;
  return (
    <span className={`${color} text-xs font-medium flex items-center gap-0.5`}>
      <Icon className="w-3 h-3" /> {value > 0 ? '+' : '-'}{formatted}
    </span>
  );
}

// Modifier constants moved to /components/compare/modifierConstants.js (v221)

const PALETTE = ['#0F2040', '#4ABFCF', '#F59E0B', '#8B5CF6', '#EC4899'];

const PHASE_COLORS = {
  'Initiation': '#0F2040',
  'Design / Discovery': '#2563EB',
  'Execution': '#4ABFCF',
  'Validation (UAT)': '#F59E0B',
  'Go Live': '#10B981',
  'Transition - Go-Live, Hypercare': '#8B5CF6',
};

// ── Planner Overlay (Compare side-by-side timeline) ─────────
function PlannerOverlayCard({ estimates }) {
  const rows = estimates.map(e => ({
    id: e.id,
    name: e.name,
    ts: e.timeline_summary || null,
    is_winner: e.is_winner || false,
  }));

  const durations = rows.map(r => (r.ts?.total_duration_weeks || 0));
  const maxWeeks = Math.max(1, ...durations);
  const baseDuration = rows[0]?.ts?.total_duration_weeks ?? null;
  const basePeakFte = rows[0]?.ts?.peak_fte ?? null;

  const fmtDelta = (cur, base, unit = '') => {
    if (cur == null || base == null || cur === base) return null;
    const delta = cur - base;
    const positive = delta > 0;
    return (
      <span className={`ml-1 text-[10px] font-medium ${positive ? 'text-red-600' : 'text-emerald-600'}`}>
        {positive ? '+' : ''}{typeof delta === 'number' ? Number(delta.toFixed(2)) : delta}{unit}
      </span>
    );
  };

  return (
    <Card data-testid="planner-overlay-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Clock className="w-4 h-4 text-slate-500" />
          Planner Overlay — Timeline &amp; Phases
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-5">
        {/* Summary grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="planner-overlay-summary">
          {rows.map((r, i) => {
            const ts = r.ts;
            if (!ts) {
              return (
                <div key={r.id} className="border border-dashed border-slate-200 rounded-md p-3 bg-slate-50/50">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                    <span className="text-xs font-medium text-slate-700 truncate">{r.name}</span>
                  </div>
                  <div className="text-[11px] text-slate-400">No planner data</div>
                </div>
              );
            }
            return (
              <div
                key={r.id}
                className="border border-slate-200 rounded-md p-3"
                data-testid={`planner-overlay-row-${i}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                  <span className="text-xs font-medium text-slate-800 truncate" title={r.name}>{r.name}</span>
                  {r.is_winner && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-1.5 py-0.5 ml-auto"
                      data-testid={`winner-badge-${i}`}
                    >
                      <Trophy className="w-2.5 h-2.5" /> Winner
                    </span>
                  )}
                </div>
                <dl className="grid grid-cols-2 gap-y-1 text-[11px]">
                  <dt className="text-slate-500">Duration</dt>
                  <dd className="font-semibold text-slate-900 text-right">
                    {ts.total_duration_weeks ?? '—'} wk
                    {i > 0 && fmtDelta(ts.total_duration_weeks, baseDuration, ' wk')}
                  </dd>
                  <dt className="text-slate-500">Peak FTE</dt>
                  <dd className="font-semibold text-slate-900 text-right">
                    {ts.peak_fte ?? '—'}
                    {i > 0 && fmtDelta(ts.peak_fte, basePeakFte, '')}
                  </dd>
                  <dt className="text-slate-500">Avg FTE</dt>
                  <dd className="font-semibold text-slate-900 text-right">{ts.avg_fte ?? '—'}</dd>
                  <dt className="text-slate-500">Peak hrs/wk</dt>
                  <dd className="font-semibold text-slate-900 text-right">{ts.peak_hours_per_week ?? '—'}</dd>
                  <dt className="text-slate-500">Phases</dt>
                  <dd className="font-semibold text-slate-900 text-right">{(ts.phases || []).length}</dd>
                </dl>
              </div>
            );
          })}
        </div>

        {/* Phase bands (mini Gantt per estimate) */}
        <div className="space-y-3" data-testid="planner-overlay-bands">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-medium text-slate-600 uppercase tracking-wide">Phase Bands</div>
            <div className="text-[10px] text-slate-400">0 — {maxWeeks} wk</div>
          </div>
          <div className="space-y-2">
            {rows.map((r, i) => {
              const ts = r.ts;
              const phases = ts?.phases || [];
              return (
                <div key={r.id} data-testid={`planner-overlay-band-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                    <span className="text-[11px] font-medium text-slate-700 truncate" title={r.name}>{r.name}</span>
                    {ts?.total_duration_weeks != null && (
                      <span className="text-[10px] text-slate-400">({ts.total_duration_weeks} wk)</span>
                    )}
                  </div>
                  <div className="relative h-6 bg-slate-50 rounded border border-slate-100 overflow-hidden">
                    {phases.map((p, pi) => {
                      const sw = p.start_week || 0;
                      const ew = p.end_week || 0;
                      if (!ew) return null;
                      const left = ((sw - 1) / maxWeeks) * 100;
                      const width = ((ew - sw + 1) / maxWeeks) * 100;
                      const color = PHASE_COLORS[p.phase] || '#64748B';
                      const label = `${p.phase}: wk ${sw}–${ew} (${p.duration_weeks ?? ''} wk)`;
                      return (
                        <div
                          key={`${p.phase}-${pi}`}
                          title={label}
                          className="absolute top-0 h-full flex items-center justify-center text-[9px] text-white/95 font-medium truncate px-1"
                          style={{
                            left: `${Math.max(0, left)}%`,
                            width: `${Math.max(1, width)}%`,
                            background: color,
                            opacity: 0.88,
                            borderRight: '1px solid rgba(255,255,255,0.35)',
                          }}
                        >
                          {p.duration_weeks != null && p.duration_weeks >= 4 ? p.phase : ''}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Legend */}
          <div className="flex flex-wrap gap-3 pt-1" data-testid="planner-overlay-legend">
            {Object.entries(PHASE_COLORS).map(([name, color]) => (
              <span key={name} className="flex items-center gap-1 text-[10px] text-slate-600">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
                {name}
              </span>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// What-If Editor moved to /components/compare/WhatIfEditor.js (v221)

// ── Compare Page ─────────────────────────────────────────────
export default function ComparePage({ initialEstimateId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [viewMode, setViewMode] = useState('effort'); // effort | cost
  const [cloning, setCloning] = useState(false);
  const [whatIfOpen, setWhatIfOpen] = useState(false);
  const [whatIfId, setWhatIfId] = useState(null);
  const [whatIfSourceName, setWhatIfSourceName] = useState('');
  const [components, setComponents] = useState([]);
  const [whatIfState, setWhatIfState] = useState(null);
  const [baseModifiers, setBaseModifiers] = useState({});
  const [recalculating, setRecalculating] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savedComparisons, setSavedComparisons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [guideResetKey, setGuideResetKey] = useState(0);
  const [exportOpen, setExportOpen] = useState(false);
  const [nominateOpen, setNominateOpen] = useState(false);
  const [nominateDefaultId, setNominateDefaultId] = useState('');
  const [publicCloudReleases, setPublicCloudReleases] = useState([]);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Load estimates + components + settings + saved comparisons
  useEffect(() => {
    const loadData = async () => {
      try {
        const [estRes, compRes, settRes, savedRes] = await Promise.all([
          axios.get(`${API_URL}/api/estimates`, { withCredentials: true }),
          axios.get(`${API_URL}/api/components`, { withCredentials: true }),
          axios.get(`${API_URL}/api/settings`, { withCredentials: true }),
          axios.get(`${API_URL}/api/comparisons`, { withCredentials: true }),
        ]);
        setEstimates(estRes.data.estimates || []);
        setComponents(compRes.data.components || []);
        setSettings(settRes.data);
        setSavedComparisons(savedRes.data.comparisons || []);
        // Public Cloud release calendar feeds the Release Impact Heatmap.
        // Failure is non-fatal — heatmap renders an "no events" hint.
        try {
          const relRes = await axios.get(`${API_URL}/api/settings/public-cloud-releases`, { withCredentials: true });
          setPublicCloudReleases(relRes.data?.events || []);
        } catch {
          setPublicCloudReleases([]);
        }

        // Check URL params for loading a saved comparison or estimate IDs
        const loadId = searchParams.get('load');
        if (loadId) {
          const saved = savedRes.data.comparisons?.find(c => c.id === loadId);
          if (saved) {
            setSelectedIds(saved.estimate_ids);
            if (saved.narrative) setNarrative(saved.narrative);
          }
        } else {
          const ids = searchParams.get('ids')?.split(',').filter(Boolean) || [];
          if (ids.length >= 2) setSelectedIds(ids);
          else if (initialEstimateId) setSelectedIds([initialEstimateId]);
        }
      } catch (err) {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Auto-compare when 2+ selected
  useEffect(() => {
    if (selectedIds.length >= 2) {
      runComparison();
    } else {
      setComparison(null);
      setNarrative(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds]);

  const runComparison = useCallback(async () => {
    setComparing(true);
    setNarrative(null);
    try {
      const res = await axios.post(`${API_URL}/api/compare`, { estimate_ids: selectedIds }, { withCredentials: true });
      setComparison(res.data);
    } catch (err) {
      toast.error('Comparison failed');
    } finally {
      setComparing(false);
    }
  }, [selectedIds]);

  const generateNarrative = async () => {
    if (!comparison?.comparisons?.[0]) return;
    setNarrativeLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/compare/narrative`, {
        estimate_ids: selectedIds,
        diff_summary: comparison.comparisons[0].diff,
      }, { withCredentials: true });
      setNarrative(res.data.narrative);
    } catch (err) {
      toast.error('AI narrative generation failed');
    } finally {
      setNarrativeLoading(false);
    }
  };

  const cloneEstimate = async (estimateId) => {
    setCloning(true);
    try {
      const res = await axios.post(`${API_URL}/api/compare/clone`, { estimate_id: estimateId }, { withCredentials: true });
      toast.success('Estimate cloned — open the editor below to modify');
      const clonedEst = res.data.estimate;
      setEstimates(prev => [clonedEst, ...prev]);
      setSelectedIds(prev => [...prev.filter(id => id !== res.data.id), res.data.id]);
      // Open the What-If editor with the clone's data
      openWhatIfEditor(clonedEst);
    } catch (err) {
      toast.error('Clone failed');
    } finally {
      setCloning(false);
    }
  };

  const openWhatIfEditor = (est) => {
    // Find the source estimate name
    const sourceId = est.clone_of;
    const sourceEst = estimates.find(e => e.id === sourceId);
    setWhatIfSourceName(sourceEst?.estimate_name || 'Original');
    setBaseModifiers(sourceEst?.modifiers || {});

    // Build whatIfState from the estimate's saved data
    const selComps = (est.selected_components || []);
    const selectedNames = selComps.map(c => c.component_name || c.name || '');
    const quantities = {};
    selComps.forEach(c => { quantities[c.component_name || c.name] = c.quantity || 1; });

    setWhatIfState({
      implementation_type: est.implementation_type || 'standard',
      complexity: est.complexity || 'standard',
      hosting_model: est.hosting_model || 'public_cloud',
      modifiers: est.modifiers || {},
      selectedNames,
      quantities,
      rate_card_override: est.rate_card_override || null,
    });
    setWhatIfId(est.id);
    setWhatIfOpen(true);
  };

  const recalculateWhatIf = async () => {
    if (!whatIfId || !whatIfState) return;
    setRecalculating(true);
    try {
      const selected_components = whatIfState.selectedNames.map(name => ({
        component_name: name,
        quantity: whatIfState.quantities[name] || 1,
      }));

      const res = await axios.put(`${API_URL}/api/compare/whatif/${whatIfId}`, {
        selected_components,
        modifiers: whatIfState.modifiers,
        implementation_type: whatIfState.implementation_type,
        complexity: whatIfState.complexity,
        hosting_model: whatIfState.hosting_model,
        rate_card_override: whatIfState.rate_card_override,
      }, { withCredentials: true });

      // Update the estimate in our list
      const updatedEst = res.data.estimate;
      setEstimates(prev => prev.map(e => e.id === updatedEst.id ? updatedEst : e));
      toast.success('What-If recalculated — comparison updated');
      // Re-run comparison
      runComparison();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recalculation failed');
    } finally {
      setRecalculating(false);
    }
  };

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const addEstimate = (id) => {
    if (id && !selectedIds.includes(id)) setSelectedIds(prev => [...prev, id]);
  };

  const removeEstimate = (id) => {
    setSelectedIds(prev => prev.filter(i => i !== id));
  };

  const saveComparison = async () => {
    if (selectedIds.length < 2) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/comparisons`, {
        estimate_ids: selectedIds,
        narrative: narrative || null,
      }, { withCredentials: true });
      setSavedComparisons(prev => [res.data, ...prev]);
      toast.success(`Comparison saved as "${res.data.name}"`);
    } catch (err) {
      toast.error('Failed to save comparison');
    } finally {
      setSaving(false);
    }
  };

  const loadComparison = (saved) => {
    setSelectedIds(saved.estimate_ids);
    if (saved.narrative) setNarrative(saved.narrative);
    setShowSaved(false);
    toast.success(`Loaded: ${saved.name}`);
  };

  // ── Register ⌘K palette commands for the Compare page ─────────
  // Available commands depend on the current selection state:
  //  - "Add {estimate} to comparison" for unselected estimates (top 8)
  //  - "Remove {estimate}" for selected ones
  //  - "Run comparison now" / "Clear selection"
  //  - "Open saved comparison: {name}" for the user's saved set
  usePaletteCommands(
    'compare-actions',
    () => {
      const items = [];
      if (selectedIds.length >= 2) {
        items.push({
          id: 'compare-run',
          value: 'Run comparison now compare diff',
          icon: Play,
          label: `Run comparison (${selectedIds.length} estimates)`,
          onSelect: () => runComparison(),
        });
      }
      if (selectedIds.length > 0) {
        items.push({
          id: 'compare-clear',
          value: 'Clear comparison selection reset',
          icon: XIcon,
          label: 'Clear comparison selection',
          onSelect: () => setSelectedIds([]),
        });
      }
      // Top-8 unselected estimates: "Add to comparison"
      estimates
        .filter((e) => !selectedIds.includes(e.id))
        .slice(0, 8)
        .forEach((e) => {
          const label = e.estimate_name || e.organization_name || 'Untitled';
          items.push({
            id: `compare-add-${e.id}`,
            value: `Add ${e.estimate_name || ''} ${e.organization_name || ''} to comparison ${e.id}`,
            icon: Plus,
            label: `Add to comparison: ${label}`,
            onSelect: () => setSelectedIds((prev) => [...prev, e.id]),
            closeOnSelect: false,  // keep palette open so user can stack adds
          });
        });
      // Currently-selected: "Remove from comparison"
      selectedIds.forEach((id) => {
        const est = estimates.find((e) => e.id === id);
        if (!est) return;
        const label = est.estimate_name || est.organization_name || 'Untitled';
        items.push({
          id: `compare-remove-${id}`,
          value: `Remove ${est.estimate_name || ''} from comparison`,
          icon: Minus,
          label: `Remove from comparison: ${label}`,
          onSelect: () => setSelectedIds((prev) => prev.filter((i) => i !== id)),
          closeOnSelect: false,
        });
      });
      // Saved comparisons (top 5)
      savedComparisons.slice(0, 5).forEach((s) => {
        items.push({
          id: `compare-saved-${s.id}`,
          value: `Saved comparison ${s.name || ''}`,
          icon: Bookmark,
          label: `Open saved: ${s.name || 'Untitled comparison'}`,
          onSelect: () => loadComparison(s),
        });
      });
      return items.length > 0
        ? { heading: 'Compare', priority: 5, items }
        : null;
    },
    { enabled: !loading, deps: [estimates, selectedIds, savedComparisons, loading] }
  );

  const deleteComparison = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/comparisons/${id}`, { withCredentials: true });
      setSavedComparisons(prev => prev.filter(c => c.id !== id));
      toast.success('Comparison deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const exportComparison = async (format) => {
    try {
      toast.info(`Generating ${format.toUpperCase()} export...`);
      const res = await axios.post(
        `${API_URL}/api/export/comparison`,
        { estimate_ids: selectedIds, narrative, format },
        { withCredentials: true, responseType: 'blob' }
      );
      const mimeMap = {
        pdf: 'application/pdf',
        pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      };
      const blob = new Blob([res.data], { type: mimeMap[format] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Comparison_Report.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  };

  const generateComparePdfBase64 = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/export/comparison`,
        { estimate_ids: selectedIds, narrative, format: 'pdf' },
        { withCredentials: true, responseType: 'blob' }
      );
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(res.data);
      });
    } catch { return null; }
  };

  // Pinned KPIs for the right rail. Reflects the *active selection*
  // (selectedIds) so the rep sees totals + ranges across whichever
  // estimates they have currently picked, even before they click
  // "Compare". Once compared, the overlay surfaces richer data.
  // Must be declared BEFORE any conditional/early return to comply
  // with React rules-of-hooks.
  const kpiMetrics = useMemo(() => {
    const selectedEstimates = (selectedIds || [])
      .map(id => (estimates || []).find(e => e.id === id))
      .filter(Boolean);
    if (selectedEstimates.length === 0) return [];
    const fmt = (n) => (n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
    const _codes = new Set(selectedEstimates.map(e => (e.currency_code || 'USD').toUpperCase()));
    const sym = _codes.size === 1 ? getCurrencySymbol([..._codes][0]) : '$';
    const efforts = selectedEstimates.map(e => e.total_effort || 0);
    const costs = selectedEstimates.map(e => e.total_cost || 0);
    const effLo = Math.min(...efforts);
    const effHi = Math.max(...efforts);
    const costLo = Math.min(...costs);
    const costHi = Math.max(...costs);
    const single = selectedEstimates.length === 1;
    const winner = selectedEstimates.find(e => e.is_winner);
    return [
      {
        label: single ? 'Effort' : 'Effort range',
        value: single ? `${fmt(effLo)} hrs` : `${fmt(effLo)}–${fmt(effHi)} hrs`,
        hint: single ? null : `Δ ${fmt(effHi - effLo)} hrs`,
        accent: 'slate',
        testId: 'effort',
      },
      {
        label: single ? 'Cost' : 'Cost range',
        value: single ? `${sym}${fmt(costLo)}` : `${sym}${fmt(costLo)}–${sym}${fmt(costHi)}`,
        hint: single ? null : `Δ ${sym}${fmt(costHi - costLo)}`,
        accent: 'cyan',
        testId: 'cost',
      },
      {
        label: 'Selected',
        value: `${selectedEstimates.length} of ${(estimates || []).length}`,
        hint: comparison ? 'Comparison loaded' : 'Pick 2+ then click Compare',
        accent: 'slate',
        testId: 'count',
      },
      {
        label: 'Winner',
        value: winner ? (winner.name?.slice(0, 22) || 'Picked') : '—',
        hint: winner ? `${sym}${fmt(winner.total_cost)} · ${fmt(winner.total_effort)} hrs` : 'Not nominated',
        accent: winner ? 'emerald' : 'slate',
        testId: 'winner',
      },
    ];
  }, [selectedIds, estimates, comparison]);


  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF]" />
      </div>
    );
  }

  const diff = comparison?.comparisons?.[0]?.diff;
  const overlay = comparison?.overlay;

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex gap-6" data-testid="compare-page">
      <div className="flex-1 min-w-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-[#0F2040] flex items-center gap-2" data-testid="compare-title">Estimate Comparison
            {!initialEstimateId && <PageHelpButton section="compare" label="Compare — open the user guide" />}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Compare estimates side-by-side to analyze differences in effort, cost, and scope</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { resetGuides(); setGuideResetKey(k => k + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); toast.success('Guides restored'); }}
            className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-600 transition-colors px-2 py-1"
            data-testid="reset-guides-btn">
            <HelpCircle className="w-3 h-3" /> Show Guides
          </button>
          <Button variant="outline" size="sm" onClick={() => setShowSaved(!showSaved)} data-testid="show-saved-btn">
            <FileText className="w-4 h-4 mr-1" />
            Saved ({savedComparisons.length})
          </Button>
          {diff && (
            <>
              <Button variant="outline" size="sm" onClick={saveComparison} disabled={saving} data-testid="save-comparison-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                Save
              </Button>
              <div className="relative">
                <Button variant="outline" size="sm" onClick={() => setExportOpen(!exportOpen)} data-testid="export-dropdown-btn">
                  <Download className="w-4 h-4 mr-1" /> Export <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
                {exportOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 py-1 min-w-[160px]">
                    {[
                      ['pdf', 'PDF Report', FileText],
                      ['docx', 'Word Document', FileText],
                      ['xlsx', 'Excel Workbook', FileText],
                      ['pptx', 'PowerPoint', FileText],
                    ].map(([fmt, label, Icon]) => (
                      <button key={fmt} onClick={() => { exportComparison(fmt); setExportOpen(false); }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                        data-testid={`export-${fmt}-btn`}>
                        <Icon className="w-4 h-4 text-slate-500" /> {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <EmailShareDialog
                onGeneratePdf={generateComparePdfBase64}
                defaultSubject="LMS Estimate Comparison Report"
                defaultFilename={`Comparison_Report_${new Date().toISOString().slice(0, 10)}.pdf`}
                triggerClassName="text-xs"
              />
              <Button variant="outline" size="sm" onClick={generateNarrative} disabled={narrativeLoading} data-testid="generate-narrative-btn">
                {narrativeLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Brain className="w-4 h-4 mr-1" />}
                AI Analysis
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Comparison Overview Guide */}
      <SectionGuide id="compare-overview" title="How Estimate Comparison Works" accent="#0F2040" icon={BookOpen} resetKey={guideResetKey}>
        <p>
          The <strong>Comparison Engine</strong> lets you place two or more estimates side-by-side to understand the real differences. It calculates deltas across effort, cost, confidence, and every component group — so you can see exactly where scope changes have the biggest impact.
        </p>
        <p>
          <strong>Workflow:</strong> Select estimates, click Compare, then drill into group-level and component-level breakdowns. Use <em>Clone for What-If</em> to create a variant of any estimate, tweak its factors or components, and compare the original against the modified version. Save comparisons for later, generate AI narratives, or export branded reports in PDF/Word/Excel/PPT.
        </p>
      </SectionGuide>

      {/* Saved Comparisons Panel */}
      {showSaved && (
        <Card className="mb-6" data-testid="saved-comparisons-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Saved Comparisons</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {savedComparisons.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">No saved comparisons yet. Run a comparison and click "Save Comparison" to keep it.</p>
            ) : (
              <div className="space-y-2">
                {savedComparisons.map(saved => (
                  <div key={saved.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg border bg-white hover:bg-slate-50 transition-colors" data-testid={`saved-comparison-${saved.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#0F2040] truncate">{saved.name}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {saved.created_by_name} &middot; {new Date(saved.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {saved.narrative && <span className="ml-1.5 text-[#4ABFCF]">&middot; AI narrative included</span>}
                      </div>
                    </div>
                    <div className="flex gap-1.5 shrink-0 ml-3">
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => loadComparison(saved)} data-testid={`load-comparison-${saved.id}`}>
                        Load
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:text-red-700" onClick={() => deleteComparison(saved.id)} data-testid={`delete-comparison-${saved.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Estimate Selector */}
      <SectionGuide id="compare-selection" title="Selecting Estimates" accent="#0EA5E9" resetKey={guideResetKey}>
        <p>
          Pick at least <strong>two estimates</strong> to compare. The first becomes the <em>BASE</em> — all deltas are calculated relative to it. Use <em>Clone for What-If</em> to create an editable copy where you can change factors, toggle components, or adjust quantities without affecting the original.
        </p>
      </SectionGuide>
      <Card className="mb-6" data-testid="estimate-selector-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            {/* Selected chips */}
            {selectedIds.map((id, idx) => {
              const est = estimates.find(e => e.id === id);
              const isClone = est?.is_clone;
              const sourceEst = isClone ? estimates.find(e => e.id === est.clone_of) : null;
              return (
                <div key={id} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border bg-white text-sm ${isClone ? 'border-amber-300 bg-amber-50/50' : ''}`} data-testid={`selected-estimate-${idx}`} title={isClone ? `Cloned from: ${sourceEst?.estimate_name || 'Unknown'}` : est?.estimate_name}>
                  <Badge variant="outline" className="text-[10px]">{idx === 0 ? 'BASE' : isClone ? 'WHAT-IF' : `#${idx + 1}`}</Badge>
                  <div className="flex flex-col min-w-0">
                    <span className="font-medium text-[#0F2040] truncate max-w-[200px]">{est?.estimate_name || 'Unknown'}</span>
                    {isClone && sourceEst && (
                      <span className="text-[10px] text-amber-600 truncate max-w-[200px]">cloned from: {sourceEst.estimate_name}</span>
                    )}
                  </div>
                  {isClone && (
                    <button onClick={() => openWhatIfEditor(est)} className="text-amber-500 hover:text-amber-700 ml-0.5" data-testid={`edit-whatif-${id}`}>
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                  <button onClick={() => removeEstimate(id)} className="text-slate-400 hover:text-red-500 ml-1 text-xs">&times;</button>
                </div>
              );
            })}
            {/* Add estimate dropdown */}
            <Select onValueChange={addEstimate}>
              <SelectTrigger className="w-[260px] text-sm" data-testid="add-estimate-select">
                <SelectValue placeholder="+ Add estimate to compare" />
              </SelectTrigger>
              <SelectContent>
                {estimates.filter(e => !selectedIds.includes(e.id)).map(est => (
                  <SelectItem key={est.id} value={est.id}>
                    <div className="flex items-center gap-2">
                      <span className="truncate">{est.estimate_name}</span>
                      <span className="text-slate-400 text-xs">{est.organization_name || ''}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Clone for What-If */}
            {isAdmin && selectedIds.length >= 1 && (
              <Button variant="outline" size="sm" onClick={() => cloneEstimate(selectedIds[0])} disabled={cloning} data-testid="clone-whatif-btn">
                {cloning ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                Clone for What-If
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ═══════════ WHAT-IF EDITOR ═══════════════════════════ */}
      {whatIfOpen && whatIfState && (
        <WhatIfEditor
          whatIfState={whatIfState}
          setWhatIfState={setWhatIfState}
          components={components}
          settings={settings}
          recalculating={recalculating}
          onRecalculate={recalculateWhatIf}
          onClose={() => { setWhatIfOpen(false); setWhatIfId(null); }}
          sourceName={whatIfSourceName}
          baseModifiers={baseModifiers}
        />
      )}

      {/* No comparison yet */}
      {!diff && !comparing && (
        <Card className="relative overflow-hidden" data-testid="compare-empty-state">
          <img
            src="/seertech-mark.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none select-none absolute -right-10 -bottom-10 w-56 h-56 opacity-[0.06]"
          />
          <CardContent className="p-12 text-center relative">
            <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-[#0F2040] mb-1">Select Estimates to Compare</h3>
            <p className="text-sm text-slate-500">Choose at least 2 estimates above to see a detailed side-by-side comparison</p>
          </CardContent>
        </Card>
      )}

      {comparing && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF] mr-2" />
          <span className="text-slate-500">Analyzing differences...</span>
        </div>
      )}

      {/* ═══════════ COMPARISON RESULTS ═══════════════════════ */}
      {diff && !comparing && (
        <div className="space-y-6">
          <SectionGuide id="compare-results" title="Reading the Comparison Results" accent="#14B8A6" resetKey={guideResetKey}>
            <p>
              The <strong>summary cards</strong> show headline deltas — effort, cost, confidence, and net impact as a percentage. Green arrows mean the compared estimate is <em>more efficient</em> (lower cost/effort); red arrows mean it's <em>more expensive</em>.
            </p>
            <p>
              The <strong>group breakdown</strong> visualizes effort or cost per component group with horizontal bars. Click any group to expand its individual components and see exactly which items changed and by how much. Use <em>AI Analysis</em> for a narrative explanation of the key differences and trade-offs.
            </p>
          </SectionGuide>
          {/* Summary Delta Cards */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-sm font-semibold text-[#0F2040]">Comparison Summary</h3>
            <MathExplainer title="Comparison Calculations" iconSize={3.5} testId="math-compare-info">
              <p>The Comparison Engine calculates deltas by subtracting the <strong>Base</strong> estimate values from the <strong>Compared</strong> estimate, after recalculating both with current system settings.</p>
              <MathSection title="Delta Formulas">
                <FormulaBlock lines={[
                  'Effort Delta = Compare Effort - Base Effort',
                  'Cost Delta = Compare Cost - Base Cost',
                  'Confidence Delta = Compare Confidence - Base Confidence',
                  'Net Impact (%) = (Cost Delta / Base Cost) × 100',
                ]} />
              </MathSection>
              <MathSection title="How Estimates Are Compared">
                <div className="space-y-2 text-xs text-slate-500">
                  <p>Both estimates are <strong>recalculated</strong> with current rate cards, component overrides, and system settings before comparison — so deltas reflect the latest configuration, not stale saved values.</p>
                  <p>The <strong>group breakdown</strong> compares effort and cost at the component-group level (e.g., Core Configuration, Integrations). Individual component deltas show which specific items drive the difference.</p>
                </div>
              </MathSection>
              <MathSection title="Delta Direction">
                <div className="space-y-1.5 text-xs">
                  <div className="flex items-start gap-2"><span className="text-emerald-500 font-bold mt-0.5">-</span><div><strong>Green (negative delta):</strong> The compared estimate uses <em>less</em> effort or costs <em>less</em> — more efficient.</div></div>
                  <div className="flex items-start gap-2"><span className="text-red-500 font-bold mt-0.5">+</span><div><strong>Red (positive delta):</strong> The compared estimate uses <em>more</em> effort or costs <em>more</em>.</div></div>
                  <div className="flex items-start gap-2"><span className="text-slate-400 font-bold mt-0.5">=</span><div><strong>Neutral:</strong> No difference.</div></div>
                </div>
              </MathSection>
              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
                <strong>Tip:</strong> Use "Clone for What-If" to create a variant of an estimate, adjust its factors or components, and compare the original vs modified version to quantify the impact of specific changes.
              </div>
            </MathExplainer>
            {comparison && selectedIds.length >= 2 && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const res = await axios.post(
                      `${API_URL}/api/compare/scenario-story-pdf`,
                      { estimate_ids: selectedIds },
                      { withCredentials: true, responseType: 'blob' }
                    );
                    const blob = new Blob([res.data], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `scenario_story_${selectedIds[0]}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                    toast.success('Scenario story downloaded');
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Could not generate PDF');
                  }
                }}
                className="ml-2 border-slate-400 text-slate-700"
                data-testid="scenario-story-pdf-btn"
              >
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                Scenario story PDF
              </Button>
            )}
            {comparison && selectedIds.length >= 2 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setNominateOpen(true)}
                className="ml-auto border-amber-400 text-amber-700 hover:bg-amber-50"
                data-testid="nominate-winner-btn"
              >
                <Trophy className="w-3.5 h-3.5 mr-1.5" />
                Nominate winner
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="summary-delta-cards">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span className="text-xs text-slate-500 uppercase tracking-wide">Effort</span>
                </div>
                <div className="text-lg font-bold text-[#0F2040]">{diff.summary.compare_total_effort?.toLocaleString()} hrs</div>
                <DeltaBadge value={diff.summary.effort_delta} invert />
                <div className="text-[10px] text-slate-400 mt-0.5">was {diff.summary.base_total_effort?.toLocaleString()} hrs</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-slate-400" />
                  <span className="text-xs text-slate-500 uppercase tracking-wide">Cost</span>
                </div>
                <div className="text-lg font-bold text-[#0F2040]">${diff.summary.compare_total_cost?.toLocaleString()}</div>
                <DeltaBadge value={diff.summary.cost_delta} format="currency" invert />
                <div className="text-[10px] text-slate-400 mt-0.5">was ${diff.summary.base_total_cost?.toLocaleString()}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-slate-400" />
                  <span className="text-xs text-slate-500 uppercase tracking-wide">Confidence</span>
                </div>
                <div className="text-lg font-bold text-[#0F2040]">{diff.summary.compare_confidence?.toFixed(0)}%</div>
                <DeltaBadge value={diff.summary.compare_confidence - diff.summary.base_confidence} format="percent" />
                <div className="text-[10px] text-slate-400 mt-0.5">was {diff.summary.base_confidence?.toFixed(0)}%</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-slate-400" />
                  <span className="text-xs text-slate-500 uppercase tracking-wide">Net Impact</span>
                </div>
                <div className={`text-lg font-bold ${diff.summary.cost_pct_change > 0 ? 'text-red-600' : diff.summary.cost_pct_change < 0 ? 'text-emerald-600' : 'text-slate-600'}`}>
                  {diff.summary.cost_pct_change > 0 ? '+' : ''}{diff.summary.cost_pct_change?.toFixed(1)}%
                </div>
                <span className="text-[10px] text-slate-400">cost change</span>
              </CardContent>
            </Card>
          </div>

          {/* View Toggle */}
          <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5 w-fit">
            <button onClick={() => setViewMode('effort')} className={`px-3 py-1 text-xs rounded-md transition-colors ${viewMode === 'effort' ? 'bg-white shadow text-[#0F2040] font-medium' : 'text-slate-500'}`} data-testid="view-effort-btn">Effort (hrs)</button>
            <button onClick={() => setViewMode('cost')} className={`px-3 py-1 text-xs rounded-md transition-colors ${viewMode === 'cost' ? 'bg-white shadow text-[#0F2040] font-medium' : 'text-slate-500'}`} data-testid="view-cost-btn">Cost ($)</button>
          </div>

          {/* Group Comparison Bars */}
          <Card data-testid="group-comparison-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Component Group Breakdown</CardTitle>
              </div>
              {/* Named Legend */}
              <div className="flex items-center gap-4 text-[11px] mt-1">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#0F2040]" /> <span className="font-medium text-[#0F2040]">{comparison.comparisons[0]?.base_name || 'Base'}</span></span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#4ABFCF]" /> <span className="font-medium text-[#4ABFCF]">{comparison.comparisons[0]?.compare_name || 'Compare'}</span></span>
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="space-y-1">
                {diff.group_diffs.map(gd => {
                  const baseVal = viewMode === 'effort' ? gd.base_effort : gd.base_cost;
                  const compVal = viewMode === 'effort' ? gd.compare_effort : gd.compare_cost;
                  const delta = viewMode === 'effort' ? gd.effort_delta : gd.cost_delta;
                  const maxVal = Math.max(...diff.group_diffs.map(g => Math.max(viewMode === 'effort' ? g.base_effort : g.base_cost, viewMode === 'effort' ? g.compare_effort : g.compare_cost))) || 1;
                  const expanded = expandedGroups[gd.group];
                  const groupComponents = diff.component_diffs.filter(c => c.group === gd.group);

                  return (
                    <div key={gd.group}>
                      <button onClick={() => toggleGroup(gd.group)} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`group-row-${gd.group.replace(/\s+/g, '-').toLowerCase()}`}>
                        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                        <span className="text-xs font-medium text-[#0F2040] w-40 text-left shrink-0">{gd.group}</span>
                        <div className="flex-1 space-y-1.5">
                          {/* Base bar */}
                          <div className="flex items-center gap-2">
                            <div className="relative w-full bg-slate-100 rounded h-5 overflow-hidden">
                              <div className="bg-[#0F2040] h-5 rounded flex items-center" style={{ width: `${Math.max((baseVal / maxVal) * 100, 2)}%` }}>
                                {(baseVal / maxVal) > 0.15 && <span className="text-[9px] text-white font-medium pl-2 whitespace-nowrap">BASE</span>}
                              </div>
                            </div>
                            <span className="text-[11px] text-[#0F2040] font-medium w-20 text-right shrink-0">{viewMode === 'cost' ? `$${baseVal.toLocaleString()}` : `${baseVal} hrs`}</span>
                          </div>
                          {/* Compare bar */}
                          <div className="flex items-center gap-2">
                            <div className="relative w-full bg-slate-100 rounded h-5 overflow-hidden">
                              <div className={`h-5 rounded flex items-center ${delta > 0 ? 'bg-red-400' : delta < 0 ? 'bg-emerald-500' : 'bg-[#4ABFCF]'}`} style={{ width: `${Math.max((compVal / maxVal) * 100, 2)}%` }}>
                                {(compVal / maxVal) > 0.15 && <span className="text-[9px] text-white font-medium pl-2 whitespace-nowrap">{delta !== 0 ? (delta > 0 ? 'COMPARE +' : 'COMPARE -') : 'COMPARE'}</span>}
                              </div>
                            </div>
                            <span className={`text-[11px] font-medium w-20 text-right shrink-0 ${delta > 0 ? 'text-red-500' : delta < 0 ? 'text-emerald-600' : 'text-[#4ABFCF]'}`}>{viewMode === 'cost' ? `$${compVal.toLocaleString()}` : `${compVal} hrs`}</span>
                          </div>
                        </div>
                        <div className="w-20 text-right shrink-0">
                          <DeltaBadge value={delta} format={viewMode === 'cost' ? 'currency' : 'number'} invert />
                        </div>
                      </button>
                      {/* Drilldown */}
                      {expanded && groupComponents.length > 0 && (
                        <div className="ml-10 mr-3 mb-2 border-l-2 border-slate-200 pl-3 space-y-0.5">
                          {groupComponents.map(cd => {
                            const bv = viewMode === 'effort' ? cd.base_effort : cd.base_cost;
                            const cv = viewMode === 'effort' ? cd.compare_effort : cd.compare_cost;
                            const d = viewMode === 'effort' ? cd.effort_delta : cd.cost_delta;
                            return (
                              <div key={cd.name} className="flex items-center gap-2 py-1.5 text-xs" data-testid={`comp-diff-${cd.name.replace(/\s+/g, '-').toLowerCase()}`}>
                                <span className="w-44 text-slate-600 truncate" title={cd.name}>{cd.name}</span>
                                <Badge variant="outline" className="text-[9px] shrink-0">{cd.role}</Badge>
                                <div className="flex-1 flex items-center gap-2">
                                  <span className="text-[#0F2040] font-medium w-16 text-right">{viewMode === 'cost' ? `$${bv.toLocaleString()}` : bv}</span>
                                  <span className="text-slate-300">&rarr;</span>
                                  <span className={`w-16 font-medium ${d > 0 ? 'text-red-500' : d < 0 ? 'text-emerald-600' : 'text-[#4ABFCF]'}`}>{viewMode === 'cost' ? `$${cv.toLocaleString()}` : cv}</span>
                                </div>
                                <div className="w-20 text-right">
                                  {d !== 0 ? <DeltaBadge value={d} format={viewMode === 'cost' ? 'currency' : 'number'} invert /> : <span className="text-slate-300 text-[10px]">—</span>}
                                </div>
                                {!cd.in_base && <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">NEW</Badge>}
                                {!cd.in_compare && <Badge className="bg-red-100 text-red-700 text-[9px]">REMOVED</Badge>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Multi-Estimate Overlay Charts */}
          {overlay && overlay.estimates.length >= 2 && (
            <div className="grid lg:grid-cols-2 gap-4">
              {/* Bar Chart - By Group */}
              <Card data-testid="overlay-bar-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Overlay: {viewMode === 'effort' ? 'Effort' : 'Cost'} by Group</CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={overlay.groups.map(g => {
                      const row = { group: g.length > 15 ? g.slice(0, 12) + '...' : g };
                      overlay.estimates.forEach((est, i) => {
                        row[est.name] = viewMode === 'effort' ? (est.effort_by_group[g] || 0) : (est.cost_by_group[g] || 0);
                      });
                      return row;
                    })}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="group" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ fontSize: 11 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {overlay.estimates.map((est, i) => (
                        <Bar key={est.id} dataKey={est.name} fill={['#0F2040', '#4ABFCF', '#F59E0B', '#8B5CF6', '#EC4899'][i % 5]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Radar Chart - By Role */}
              <Card data-testid="overlay-radar-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Overlay: {viewMode === 'effort' ? 'Effort' : 'Cost'} by Role</CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <ResponsiveContainer width="100%" height={280}>
                    <RadarChart data={overlay.roles.map(r => {
                      const row = { role: ROLE_LABELS[r] || r };
                      overlay.estimates.forEach((est) => {
                        row[est.name] = viewMode === 'effort' ? (est.effort_by_role[r] || 0) : (est.cost_by_role[r] || 0);
                      });
                      return row;
                    })}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="role" tick={{ fontSize: 9 }} />
                      <PolarRadiusAxis tick={{ fontSize: 9 }} />
                      {overlay.estimates.map((est, i) => (
                        <Radar key={est.id} name={est.name} dataKey={est.name} stroke={['#0F2040', '#4ABFCF', '#F59E0B', '#8B5CF6', '#EC4899'][i % 5]} fill={['#0F2040', '#4ABFCF', '#F59E0B', '#8B5CF6', '#EC4899'][i % 5]} fillOpacity={0.15} />
                      ))}
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Planner Overlay — side-by-side timeline comparison */}
          {overlay && overlay.estimates.length >= 2 && overlay.estimates.some(e => e.timeline_summary) && (
            <PlannerOverlayCard estimates={overlay.estimates} />
          )}

          {/* Release Impact Heatmap — calendar strip plotting each scenario's
              Go-Live position relative to Public Cloud release windows. */}
          {overlay && overlay.estimates.some(e => e.timeline_summary?.go_live_date) && (
            <ReleaseImpactHeatmap
              scenarios={overlay.estimates
                .filter(e => e.timeline_summary?.go_live_date)
                .map(e => ({
                  id: e.id,
                  name: e.name,
                  go_live_date: e.timeline_summary.go_live_date,
                  is_winner: e.is_winner,
                }))}
              releases={publicCloudReleases}
              onLockInWinner={(scenarioId, scenarioName) => {
                setNominateDefaultId(scenarioId);
                setNominateOpen(true);
                toast.message(`Pre-selected "${scenarioName}" as the proposed winner — confirm in the dialog.`);
              }}
            />
          )}

          {/* Config & Factor Diffs */}
          {(Object.keys(diff.config_diffs).length > 0 || diff.modifier_diffs.length > 0) && (
            <Card data-testid="config-factor-diffs">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Configuration & Factor Differences</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="grid gap-2">
                  {Object.entries(diff.config_diffs).map(([key, vals]) => (
                    <div key={key} className="flex items-center gap-3 text-xs py-1.5 px-3 rounded-lg bg-amber-50 border border-amber-100">
                      <span className="font-medium text-amber-800 w-40 capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="text-amber-600">{vals.base}</span>
                      <span className="text-amber-400">&rarr;</span>
                      <span className="text-amber-800 font-medium">{vals.compare}</span>
                    </div>
                  ))}
                  {diff.modifier_diffs.map(md => (
                    <div key={md.factor} className="flex items-center gap-3 text-xs py-1.5 px-3 rounded-lg bg-slate-50 border border-slate-100">
                      <span className="font-medium text-slate-700 w-40 capitalize">{md.factor.replace(/_/g, ' ')}</span>
                      <span className="text-slate-500">{md.base_value?.toFixed(2)}</span>
                      <span className="text-slate-300">&rarr;</span>
                      <span className="text-[#0F2040] font-medium">{md.compare_value?.toFixed(2)}</span>
                      <DeltaBadge value={md.delta} invert />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* AI Narrative */}
          {narrative && (
            <Card className="border-[#4ABFCF]/30 bg-gradient-to-br from-white to-[#4ABFCF]/5" data-testid="ai-narrative-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Brain className="w-4 h-4 text-[#4ABFCF]" /> AI Comparison Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 space-y-4 text-sm">
                {narrative.executive_summary && (
                  <div>
                    <h4 className="font-semibold text-[#0F2040] text-xs uppercase tracking-wide mb-1">Executive Summary</h4>
                    <p className="text-slate-600 leading-relaxed">{narrative.executive_summary}</p>
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                  {narrative.cost_impact && (
                    <div className="p-3 bg-white/60 rounded-lg border">
                      <h4 className="font-semibold text-[#0F2040] text-xs mb-1 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Cost Impact</h4>
                      <p className="text-slate-600 text-xs leading-relaxed">{narrative.cost_impact}</p>
                    </div>
                  )}
                  {narrative.timeline_impact && (
                    <div className="p-3 bg-white/60 rounded-lg border">
                      <h4 className="font-semibold text-[#0F2040] text-xs mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Timeline Impact</h4>
                      <p className="text-slate-600 text-xs leading-relaxed">{narrative.timeline_impact}</p>
                    </div>
                  )}
                </div>
                {narrative.risk_assessment && (
                  <div className="p-3 bg-white/60 rounded-lg border">
                    <h4 className="font-semibold text-[#0F2040] text-xs mb-1 flex items-center gap-1"><Shield className="w-3 h-3" /> Risk Assessment</h4>
                    <p className="text-slate-600 text-xs leading-relaxed">{narrative.risk_assessment}</p>
                  </div>
                )}
                {narrative.recommendations?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-[#0F2040] text-xs uppercase tracking-wide mb-1">Recommendations</h4>
                    <ul className="space-y-1">
                      {narrative.recommendations.map((rec, i) => (
                        <li key={`rec-${rec.slice(0, 30)}-${i}`} className="text-xs text-slate-600 flex gap-2"><span className="text-[#4ABFCF] font-bold">{i + 1}.</span> {rec}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {narrative.key_takeaway && (
                  <div className="p-3 bg-[#0F2040]/5 rounded-lg border border-[#0F2040]/10">
                    <p className="text-xs text-[#0F2040] font-medium">{narrative.key_takeaway}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Role Breakdown */}
          {diff.role_diffs?.length > 0 && (
            <Card data-testid="role-breakdown-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Role Allocation Comparison</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {diff.role_diffs.map(rd => {
                    const bv = viewMode === 'effort' ? rd.base_effort : rd.base_cost;
                    const cv = viewMode === 'effort' ? rd.compare_effort : rd.compare_cost;
                    const d = viewMode === 'effort' ? rd.effort_delta : rd.cost_delta;
                    return (
                      <div key={rd.role} className="text-center p-3 rounded-lg bg-slate-50 border">
                        <Badge variant="outline" className="text-[10px] mb-1.5">{rd.role}</Badge>
                        <div className="text-xs text-slate-500">{ROLE_LABELS[rd.role] || rd.role}</div>
                        <div className="text-sm font-bold text-[#0F2040] mt-1">{viewMode === 'cost' ? `$${cv.toLocaleString()}` : `${cv} hrs`}</div>
                        <div className="flex justify-center mt-0.5"><DeltaBadge value={d} format={viewMode === 'cost' ? 'currency' : 'number'} invert /></div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Nominate Winner dialog */}
      {comparison?.overlay?.estimates && (
        <NominateWinnerDialog
          open={nominateOpen}
          onOpenChange={setNominateOpen}
          estimates={comparison.overlay.estimates.map((e) => ({
            id: e.id,
            name: e.name,
            total_effort: e.total_effort,
            total_cost: e.total_cost,
          }))}
          defaultWinnerId={nominateDefaultId || comparison.overlay.estimates[0]?.id}
          onNominated={() => { /* dialog closes itself */ }}
        />
      )}
      </div>
      <LiveKPIRail
        storageKey="compare"
        title="Live KPIs"
        sourceLabel={selectedIds?.length > 1 ? `Compare (${selectedIds.length})` : 'Compare'}
        metrics={kpiMetrics}
      />
    </div>
  );
}
